import _ from 'lodash';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  Checkbox,
  Currency,
  DateTime,
  ExpenseLockIcon,
  Icon,
  InlineTooltip,
  ListView,
  Page,
  Table,
  MarkupIcon,
} from '~/components';
import { colors } from '~/styles';
import ExpenseAmountWidget from '../ExpenseAmountWidget';
import {
  ApprovalAction,
  ApprovalActions,
  Approve,
  Body,
  Cell,
  Details,
  List,
  ListGroup,
  ListGroupContent,
  ListGroupHeader,
  ListHeader,
  NoResultsCell,
  Notes,
  Reject,
  Row,
  SelectAll,
  ToggleSwitch,
} from './StyledComponents';

const Byline = styled.small`
  display: block;
`;

const ApproverQueueWarning = styled.div`
  position: relative;
  font-size: 1rem;
`;

function ExpenseApprovalResults({
  results,
  selection,
  isSubmitting,
  isApproverQueue,
  isAdminQueue,
  onStatusChange,
  onGroupAction,
  onResultClick,
  onChange,
  action,
  showNotes,
}) {
  const groups = useMemo(() => {
    return results.reduce((a, v) => {
      a[v.member.id] = a[v.member.id] || { member: v.member, items: [] };
      a[v.member.id].items.push(v);
      return a;
    }, {});
  }, [results]);

  const enabledItems = useMemo(() => results.filter((item) => !item.isLocked), [results]);

  const isQueue = isApproverQueue || isAdminQueue;

  return (
    <Page.ListView>
      <List>
        <ListHeader>
          <Cell style={{ width: '3rem', padding: '0.7rem 2rem 0.7rem 1.125rem' }}>
            <SelectAll>
              <Checkbox
                checked={selection.any}
                partial={selection.some}
                disabled={isSubmitting || enabledItems.length === 0 || action === 'filter' || !isQueue}
                onChange={selection.toggleAll}
              />
              {!isQueue && <ApproverQueueTooltip />}
            </SelectAll>
          </Cell>

          <Cell style={{ minWidth: '15rem', flex: '1' }}>Member</Cell>

          <Cell style={{ width: '5rem', justifyContent: 'flex-end', textAlign: 'right' }}># of Items</Cell>

          <Cell style={{ width: '8.5rem', justifyContent: 'flex-end', textAlign: 'right' }}>Client Billable</Cell>

          <Cell style={{ width: '8.5rem', justifyContent: 'flex-end', textAlign: 'right' }}>
            Client
            <br />
            Non-billable
          </Cell>

          <Cell style={{ width: '8.5rem', justifyContent: 'flex-end', textAlign: 'right' }}>Internal</Cell>

          <Cell style={{ width: '8rem', justifyContent: 'flex-end', textAlign: 'right' }}></Cell>

          <Cell style={{ width: '8.5rem', justifyContent: 'flex-end', textAlign: 'right' }}>Total</Cell>

          <Cell style={{ width: '8rem' }}>
            {!isQueue && (
              <ApproverQueueWarning>
                <Icon icon="warning" color={colors.warning} />
                <ApproverQueueTooltip />
              </ApproverQueueWarning>
            )}
          </Cell>
        </ListHeader>

        {results.length === 0 && (
          <Table.Row>
            <NoResultsCell style={{ fontSize: '0.875rem' }} fade={action === 'filter'}>
              No results
            </NoResultsCell>
          </Table.Row>
        )}

        <Body>
          {_.map(groups, (group, key) => {
            return (
              <Group
                key={key}
                group={group}
                selection={selection}
                isSubmitting={isSubmitting}
                isApproverQueue={isApproverQueue}
                isQueue={isQueue}
                onAction={onGroupAction}
                action={action}>
                {group.items.map((item) => {
                  const checked = selection.isSelected(item.id);

                  const submitting =
                    (isSubmitting === 'batch' && checked) ||
                    isSubmitting?.group?.items.some((ei) => ei.id === item.id) ||
                    isSubmitting === item.id;

                  const pendingApproval = item.statusId === 'pending_approval';

                  const limitReached = !item.isLocked && selection.isDisabled(item.id);

                  const disableCheckbox = submitting || !pendingApproval || !isQueue || item.isLocked || limitReached;
                  const disableActions = submitting || !pendingApproval || !isApproverQueue || item.isLocked;

                  const handleRowClick = () => {
                    onResultClick(item, 'edit');
                  };

                  const handleActionClick = (statusId) => {
                    onStatusChange(item, statusId);
                  };

                  return (
                    <Row key={item.id} data-testid="row" clickable={!!handleRowClick} onClick={handleRowClick}>
                      <Cell
                        style={{
                          width: '2.5rem',
                          padding: '0.5rem 0rem 0.5rem 3.5rem',
                          position: 'relative',
                        }}
                        onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={checked}
                          disabled={disableCheckbox}
                          onChange={() => selection.toggle(item.id)}
                        />
                        {item.isLocked && <LockedTooltip />}
                        {limitReached && (
                          <InlineTooltip message={`You can only select ${selection.limit} items at a time.`} />
                        )}
                      </Cell>

                      <Cell style={{ width: '2.875rem' }}>
                        {item.isLocked && <ExpenseLockIcon value={item.lockStatusId} />}
                      </Cell>

                      <Cell style={{ width: '7.5rem' }}>
                        <DateTime value={item.date} />
                      </Cell>

                      <Cell style={{ minWidth: '15rem', flex: '1', flexWrap: 'wrap' }}>
                        <Details>
                          <div>
                            {item.project && (
                              <p>
                                {item.project.name}
                                <Byline>{item.project.client.name}</Byline>
                              </p>
                            )}
                          </div>

                          <div>
                            {item.vendorName && <p>{item.vendorName}</p>}

                            {item.expenseCategory && <p>{item.expenseCategory.name}</p>}
                          </div>
                        </Details>

                        {showNotes === 'yes' && !!item.notes && (
                          <Notes>
                            <em>{item.notes}</em>
                          </Notes>
                        )}
                      </Cell>

                      <Cell style={{ flexShrink: 0, justifyContent: 'flex-end' }}>
                        <ExpenseAmountWidget item={item} showApprovalActions onApprovalChange={onChange} />
                      </Cell>

                      <Cell style={{ width: '8.5rem', justifyContent: 'flex-end', position: 'relative' }}>
                        <ApprovalActions onClick={(e) => e.stopPropagation()}>
                          <ApprovalAction>
                            <Approve
                              disabled={disableActions}
                              status={item.statusId}
                              onClick={() => handleActionClick('approved')}>
                              <Icon icon="thumbs-up" />
                              {!isApproverQueue && <ApproverQueueTooltip />}
                              {item.isLocked && <LockedTooltip />}
                            </Approve>
                          </ApprovalAction>
                          <ApprovalAction>
                            <Reject
                              disabled={disableActions}
                              status={item.statusId}
                              onClick={() => handleActionClick('rejected')}>
                              <Icon icon="thumbs-down" />
                              {!isApproverQueue && <ApproverQueueTooltip />}
                              {item.isLocked && <LockedTooltip />}
                            </Reject>
                          </ApprovalAction>
                        </ApprovalActions>
                      </Cell>
                    </Row>
                  );
                })}
              </Group>
            );
          })}
        </Body>

        <ListView.Status total={results.length} label="Expense Item" isLoading={!!action} />
      </List>
    </Page.ListView>
  );
}

const sumAmount = (item) => _.round(item.convertedAmount, 2);

const sumTotal = (item) => _.round(item.convertedTotal, 2);

function Group({ group, selection, isSubmitting, isApproverQueue, isQueue, children, onAction, action }) {
  const [collapsed, setCollapsed] = useState(true);

  const amount = useMemo(() => {
    return {
      clientBillable: _.round(
        _(group.items)
          .filter((item) => item.project?.client?.isInternal === false && item.isBillable === true)
          .sumBy(sumAmount),
        2,
      ),
      clientNonbillable: _.round(
        _(group.items)
          .filter((item) => item.project?.client?.isInternal === false && item.isBillable === false)
          .sumBy(sumAmount),
        2,
      ),
      internal: _.round(
        _(group.items)
          .filter((item) => !item.project || item.project?.client?.isInternal === true)
          .sumBy(sumAmount),
        2,
      ),
      total: _.round(_(group.items).sumBy(sumAmount), 2),
      revenue: _.round(_(group.items).sumBy(sumTotal), 2),
    };
  }, [group]);

  const enabledItems = useMemo(() => group.items.filter((item) => !item.isLocked), [group.items]);

  const groupSelection = useMemo(() => {
    return selection.ids.filter((id) => group.items.some((item) => item.id === id));
  }, [group, selection.ids]);

  const groupSelected = useMemo(() => {
    return group.items.some((e) => groupSelection.includes(e.id));
  }, [group, groupSelection]);

  const handleToggleClick = () => {
    setCollapsed(!collapsed);
  };

  const handleGroupSelectionChange = () => {
    if (groupSelected) groupSelection.forEach((id) => selection.deselect(id));
    else selection.selectMany(enabledItems.map(({ id }) => id));
  };

  const status = useMemo(() => {
    return {
      hasApproved: _.some(enabledItems, (item) => item.statusId === 'approved'),
      hasRejected: _.some(enabledItems, (item) => item.statusId === 'rejected'),
      hasPending: _.some(enabledItems, (item) => item.statusId === 'pending_approval'),
      hasNotSubmitted: _.some(enabledItems, (entry) => entry.statusId === 'not_submitted'),

      pendingApproval: _.every(enabledItems, (entry) => entry.statusId === 'pending_approval'),

      get approved() {
        if (!this.hasApproved) return null;
        if (this.hasApproved && !this.hasRejected && !this.hasPending && !this.hasNotSubmitted) return 'approved';
        return 'partially_approved';
      },

      get rejected() {
        if (!this.hasRejected) return null;
        if (this.hasRejected && !this.hasApproved && !this.hasPending && !this.hasNotSubmitted) return 'rejected';
        return 'partially_rejected';
      },
    };
  }, [enabledItems]);

  const handleActionClick = (statusId) => {
    onAction({ ...group, items: enabledItems.filter((e) => e.statusId === 'pending_approval') }, statusId);
  };

  const submitting = isSubmitting?.group?.member.id === group.member.id || (isSubmitting === 'batch' && groupSelected);
  const hasLockedItem = group.items.some((item) => item.isLocked);

  const disableCheckbox = submitting || enabledItems.length === 0 || !status.pendingApproval || !isQueue;
  const disableActions =
    submitting || enabledItems.length === 0 || !status.pendingApproval || !isApproverQueue || hasLockedItem;

  return (
    <ListGroup fade={action === 'filter'}>
      <ListGroupHeader data-testid="group" onClick={handleToggleClick}>
        <Cell flex="0" padding="0.5rem 0.25rem 0.5rem 0.75rem">
          <ToggleSwitch>
            <Icon color={colors.grey25} icon={collapsed ? 'chevron-right' : 'chevron-down'} />
          </ToggleSwitch>
        </Cell>

        <Cell flex="0" padding="0" style={{ position: 'relative' }} onClick={(event) => event.stopPropagation()}>
          <Checkbox
            disabled={disableCheckbox}
            checked={groupSelected}
            partial={groupSelection.length < enabledItems.length}
            onChange={handleGroupSelectionChange}
          />
          {!isQueue && <ApproverQueueTooltip />}
        </Cell>

        <Cell style={{ minWidth: '15rem', flex: '1' }}>{group.member.name}</Cell>

        <Cell style={{ width: '8.5rem', justifyContent: 'flex-end', textAlign: 'right' }}>{group.items.length}</Cell>

        <Cell style={{ width: '8.5rem', justifyContent: 'flex-end', textAlign: 'right', color: colors.success }}>
          <Currency value={amount.clientBillable} />
        </Cell>

        <Cell style={{ width: '8.5rem', justifyContent: 'flex-end', textAlign: 'right', color: colors.danger }}>
          <Currency value={amount.clientNonbillable} />
        </Cell>

        <Cell style={{ width: '8.5rem', justifyContent: 'flex-end', textAlign: 'right', color: colors.warning }}>
          <Currency value={amount.internal} />
        </Cell>

        <Cell style={{ width: '16.5rem', justifyContent: 'flex-end', textAlign: 'right' }}>
          <MarkupIcon
            style={{ marginRight: '1rem' }}
            tooltip={
              <div>
                With markup to <Currency value={amount.revenue} />
              </div>
            }
          />
          <Currency value={amount.total} />
        </Cell>

        <Cell style={{ width: '8rem', justifyContent: 'flex-end' }}>
          <ApprovalActions onClick={(e) => e.stopPropagation()}>
            <ApprovalAction>
              <Approve disabled={disableActions} status={status.approved} onClick={() => handleActionClick('approved')}>
                <Icon icon="thumbs-up" />
              </Approve>
            </ApprovalAction>
            <ApprovalAction>
              <Reject disabled={disableActions} status={status.rejected} onClick={() => handleActionClick('rejected')}>
                <Icon icon="thumbs-down" />
              </Reject>
            </ApprovalAction>
          </ApprovalActions>
        </Cell>
      </ListGroupHeader>

      {!collapsed && <ListGroupContent>{children}</ListGroupContent>}
    </ListGroup>
  );
}

function ApproverQueueTooltip() {
  return <InlineTooltip message="Select an Approver to enable approval actions." />;
}

function LockedTooltip() {
  return <InlineTooltip message="This item is locked based on your security role." />;
}

export default ExpenseApprovalResults;
